import { GridDataService } from './grid-data.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageHandlerService } from '../../utility/message-handler.service';
import { remove } from 'lodash';
import { ColumnSettings } from '../../model/column-settings';
import { GridQueryUtils } from '../../utility/grid-query-utils';

@Injectable()
export class MemberListService extends GridDataService
{
    public translateService: TranslateService;

    constructor(http: HttpClient, translate: TranslateService, messageHandlerService: MessageHandlerService)
    {
        super(http, '/members', translate, messageHandlerService);
        this.translateService = translate;
    }

    public setDefaultColumnsSetting(filterState: string, filterColumns: string, defaultPageSize: number, patientLabel: string,
        insuranceEnabled: boolean = null, communityEnabled: boolean = null): Promise<any[]>
    {
        return new Promise((resolve: any) =>
        {
            this.translateService.get(['NAME', 'PREFERRED-NAME', 'BUSINESS-PHONE', 'DATE-OF-BIRTH', 'GENDER', 'LANGUAGES',
                'PATIENT-ID', 'INSURER', 'INSURER-MEMBER-ID', 'GROUP-ID', 'COMMUNITY-INVITE', 'CONSENT', 'PATIENT-FLAGS',
                'COMPOSITE-RISK-SCORE'])
                .subscribe({
                    next: (res) =>
                    {
                        const columnsConfig: ColumnSettings[] = [
                            {
                                field: 'fullName',
                                title: res.NAME,
                                titleKeys: ['NAME'],
                                width: 100,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'preferredName',
                                title: res['PREFERRED-NAME'],
                                titleKeys: ['PREFERRED-NAME'],
                                width: 100,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'homePhoneNumber',
                                title: res['BUSINESS-PHONE'],
                                titleKeys: ['BUSINESS-PHONE'],
                                width: 100,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'dateOfBirth',
                                title: res['DATE-OF-BIRTH'],
                                titleKeys: ['DATE-OF-BIRTH'],
                                width: 80,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                filter: 'date',
                                format: '{0:d}'
                            },
                            {
                                field: 'gender',
                                title: res.GENDER,
                                titleKeys: ['GENDER'],
                                width: 60,
                                sortable: true,
                                filterable: true,
                                hidden: false,
                                columnMenu: false
                            },
                            {
                                field: 'spokenLanguages',
                                title: res.LANGUAGES,
                                titleKeys: ['LANGUAGES'],
                                width: 100,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'id',
                                title: res['PATIENT-ID'],
                                titleKeys: ['PATIENT-ID'],
                                width: 60,
                                filter: 'numeric',
                                format: '#',
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'insurer',
                                title: res.INSURER,
                                titleKeys: ['INSURER'],
                                width: 100,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'insuranceMemberId',
                                title: res['INSURER-MEMBER-ID'],
                                titleKeys: ['INSURER-MEMBER-ID'],
                                width: 80,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'groupId',
                                title: res['GROUP-ID'],
                                titleKeys: ['GROUP-ID'],
                                width: 80,
                                sortable: true,
                                filterable: true,
                                hidden: false
                            },
                            {
                                field: 'inviteStatus',
                                title: res['COMMUNITY-INVITE'],
                                titleKeys: ['COMMUNITY-INVITE'],
                                width: 50,
                                sortable: false,
                                filterable: false,
                                hidden: false,
                                class: 'text-center'
                            },
                            {
                                field: 'consent',
                                title: res.CONSENT,
                                titleKeys: ['CONSENT'],
                                width: 50,
                                sortable: false,
                                filterable: false,
                                hidden: false,
                                class: 'text-center'
                            },
                            {
                                field: 'memberFlagsText',
                                title: res['PATIENT-FLAGS'],
                                titleKeys: ['PATIENT-FLAGS'],
                                width: 50,
                                sortable: true,
                                filterable: true,
                                hidden: true
                            },
                            {
                                field: 'compositeRiskScore',
                                title: res['COMPOSITE-RISK-SCORE'],
                                titleKeys: ['COMPOSITE-RISK-SCORE'],
                                width: 25,
                                filter: 'numeric',
                                format: '{0:n}',
                                sortable: true,
                                filterable: true,
                                hidden: true,
                                class: 'text-center'
                            }
                        ];

                        // Set grid columns
                        const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
                            filterColumns, columnsConfig, patientLabel);

                        // Removed columns if option not enabled (for setting edit, insuranceEnabled and communityEnabled are null)
                        if (insuranceEnabled !== null && !insuranceEnabled)
                        {
                            remove(loadedColumns, (x: ColumnSettings) =>
                                x.field === 'insurer' || x.field === 'insuranceMemberId' || x.field === 'groupId');
                        }

                        if (communityEnabled !== null && !communityEnabled)
                        {
                            remove(loadedColumns, (x: ColumnSettings) => x.field === 'inviteStatus' || x.field === 'consent');
                        }

                        resolve({
                            state: filterState ? JSON.parse(filterState) : { skip: 0, group: [], sort: [], take: defaultPageSize },
                            columns: loadedColumns
                        });
                    }
                });
        });
    }
}
